import GlobalSettingsAPI from "@/api/global-settings.api"

const SHOW_INSIGHTS_SETTING = "ShowInsights"

// The only gate on the softphone campaign feature. Must exist as a PUBLIC bool global setting or
// the public settings endpoint refuses to serve it - an unset, non-public or unreadable flag
// resolves to false, which keeps the feature hidden.
const SOFTPHONE_GOES_LIVE_SETTING = "SOFTPHONE_GOES_LIVE"

const CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

// These settings gate features, so the coercion has to fail CLOSED. Boolean() would not:
// Boolean('false') is true, which would switch a feature on if the endpoint ever answered with a
// string. Only a real true - or the string 'true' - counts.
function parseBoolSetting(value) {
  return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true')
}

const state = {
  // Keyed by setting id so a new bool flag needs no new state, mutations or getters.
  boolSettings: {},
  boolSettingsFetchedAt: {}
}

const getters = {
  boolSetting: (state) => (settingId) => state.boolSettings[settingId] ?? false,

  isBoolSettingCacheValid: (state) => (settingId) => {
    const fetchedAt = state.boolSettingsFetchedAt[settingId]

    if (!fetchedAt) {
      return false
    }

    return (Date.now() - fetchedAt) < CACHE_DURATION_MS
  },

  shouldShowInsights: (state) => state.boolSettings[SHOW_INSIGHTS_SETTING] ?? false,

  // Whether the softphone feature is live at all. Not sufficient on its own - the campaign
  // flows are per-user, see isSoftphoneUser.
  isSoftphoneEnabled: (state) => state.boolSettings[SOFTPHONE_GOES_LIVE_SETTING] ?? false,

  // A softphone user: the feature is live AND this user is a softphone agent. Softphone users
  // get the softphone campaign flows instead of - not in addition to - the standard ones.
  isSoftphoneUser: (state, getters, rootState, rootGetters) =>
    getters.isSoftphoneEnabled && rootGetters['auth/isSoftphoneAgent'] === true
      && rootGetters['auth/hasPermission']?.('campaigns') === true,

  // Whether the softphone campaigns on the current account may be READ and EDITED here. A
  // softphone agent qualifies through their own grant; a super-admin impersonating the account
  // qualifies because they are staff operating it, and the account's agents are not them.
  //
  // Deliberately a SECOND getter rather than a widening of `isSoftphoneUser`. That one is the
  // entitlement the softphone widget itself keys off (see `softphone/isSoftphoneAvailable`), so
  // widening it in place would have an impersonating admin's browser register as a softphone and
  // start taking the account's calls. Visibility travels with impersonation; the SIP identity does
  // not. Still ANDed with the global flag, so the kill switch hides the campaigns from everyone.
  canViewSoftphoneCampaigns: (state, getters, rootState, rootGetters) =>
    getters.isSoftphoneEnabled
      && rootGetters['auth/hasPermission']?.('campaigns') === true
      && (rootGetters['auth/isSoftphoneAgent'] === true || rootGetters['auth/isImpersonating'] === true),

  // Whether a softphone campaign may be CREATED here, which is narrower than being a softphone
  // user: never while impersonating, even for staff who are softphone agents in their own right.
  // The backend composes a new campaign's destination from the ACTING user
  // (GetSoftphoneAgentId(accountId, createdByUserId)), so a campaign an admin created on someone
  // else's account would ring an agent id with no Telnyx credential and fail its Ringba sync
  // forever. Nothing server-side refuses this - the create path only checks the acting user id is
  // present - so this gate is the only thing standing between an agent-admin and a dead campaign.
  canCreateSoftphoneCampaign: (state, getters, rootState, rootGetters) =>
    getters.isSoftphoneUser && rootGetters['auth/isImpersonating'] !== true
}

const actions = {
  async getBoolSetting({ commit, getters }, settingId) {
    if (getters.isBoolSettingCacheValid(settingId)) {
      return getters.boolSetting(settingId)
    }

    let value = false

    try {
      const response = await GlobalSettingsAPI.getBoolSettingValue({ settingId })
      value = parseBoolSetting(response.data)
    } catch {
      // A missing, non-public or unreachable setting means the feature stays off.
      value = false
    }

    commit('SET_BOOL_SETTING', { settingId, value })

    return value
  },

  getInsightsSetting({ dispatch }) {
    return dispatch('getBoolSetting', SHOW_INSIGHTS_SETTING)
  },

  getSoftphoneGoesLiveSetting({ dispatch }) {
    return dispatch('getBoolSetting', SOFTPHONE_GOES_LIVE_SETTING)
  }
}

const mutations = {
  SET_BOOL_SETTING(state, { settingId, value }) {
    state.boolSettings = { ...state.boolSettings, [settingId]: value }
    state.boolSettingsFetchedAt = { ...state.boolSettingsFetchedAt, [settingId]: Date.now() }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
